import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const base64ToFile = (
  base64String: string,
  fileName: string,
  mimeType: string
): File => {
  // Remove data URL prefix if present (e.g., "data:image/png;base64,")
  const base64Data = base64String.replace(/^data:[^;]+;base64,/, "");

  // Convert base64 to binary string
  const binaryString = atob(base64Data);

  // Create Uint8Array from binary string
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // Create and return File object
  return new File([bytes], fileName, { type: mimeType });
};

export function getDateFromMinutes(minutes: number) {
  const now = new Date();
  now.setHours(0, 0, 0, 0); // Set time to midnight
  now.setMinutes(minutes);
  return now;
}

export const formatMinutesToHours = (minutes: number) => {
  // Handle invalid inputs
  if (!Number.isFinite(minutes)) {
    return "00 Hrs 00 Min";
  }

  // Use absolute value for calculations, then apply sign
  const absMinutes = Math.abs(minutes);
  const hrs = Math.floor(absMinutes / 60);
  const mins = Math.floor(absMinutes % 60);
  const sign = minutes < 0 ? "-" : "";

  return `${sign}${hrs.toString().padStart(2, "0")} Hrs ${mins.toString().padStart(2, "0")} Min`;
};
export const sanitizeText = (s?: string) =>
  (s ?? "")
    .normalize?.("NFD") // separa diacríticos
    .replace(/[\u0300-\u036f]/g, "") // remueve diacríticos
    .replace(/\s+/g, "") // remueve espacios
    .replace(/[^a-zA-Z0-9]/g, "") // remueve caracteres no alfanuméricos
    .toLowerCase() ?? "";

export const convertMetersToMiles = (meters: number) => {
  const base = 1609.344;
  if (meters == undefined) return -1;
  if (meters <= 0) return 0;
  return meters / base;
};

export const toCurrencyFormat = (
  amount: number,
  locale: string = "en-US",
  currency: string = "USD"
) => {
  return (
    "$" +
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    })
      .format(amount)
      .replace(/[^0-9.,]/g, "")
  );
};

export const formatCurrency = (
  amount: number,
  locale: string = "en-US",
  currency: string = "USD"
): string => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatNumber = (
  value: number,
  locale: string = "en-US",
  options?: Intl.NumberFormatOptions
): string => {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
  }).format(value);
};
